// Generated by Claude Code
import { AutomationDetailPanel } from '#components/domain/operations/automations/detail/automation-detail-panel.tsx'
import { AutomationUpsertDrawer } from '#components/domain/operations/automations/shared/automation-edit/automation-upsert-drawer.tsx'
import { ConfirmModal } from '#components/primitives/components/confirm-modal.tsx'
import { PermissionGuard } from '#components/tools/permission-guard.tsx'
import AutomationsApi from '#core/api/operations/Automations.ts'
import { PermissionAction, PermissionService } from '#core/entities/auth/roles/Permission.ts'
import type { Automation } from '#core/entities/operations/automations/Automation.ts'
import { useUserState } from '#core/state/UserState.ts'
import { useDeleteConfirm } from '#hooks/use-delete-confirm.ts'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ArrowLeft } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

export const Route = createFileRoute('/_app/$companyId/automations/$automationId')({
  component: RouteComponent,
  staticData: {
    title: 'Automation Detail',
  },
})

function RouteComponent() {
  return (
    <PermissionGuard
      permissions={[[[PermissionService.AUTOMATIONS, PermissionAction.READ]]]}
      redirectOnNotAuthorized
    >
      <AutomationDetailPageContent />
    </PermissionGuard>
  )
}

function AutomationDetailPageContent() {
  const { companyId, automationId } = Route.useParams()
  const navigate = useNavigate()
  const { t } = useTranslation('automations')

  const userState = useUserState()
  const canUpdate = userState.hasPermission(PermissionService.AUTOMATIONS, PermissionAction.UPDATE)
  const canDelete = userState.hasPermission(PermissionService.AUTOMATIONS, PermissionAction.DELETE)
  const deleteConfirm = useDeleteConfirm<Automation>()
  const [editing, setEditing] = useState<Automation | undefined>(undefined)
  const [reloadKey, setReloadKey] = useState(0)

  function handleBack() {
    void navigate({ to: '/$companyId/automations', params: { companyId } })
  }

  return (
    <div className="container-fluid d-flex flex-column h-100 py-3">
      <div className="row justify-content-center flex-grow-1">
        <div className="col-12 col-lg-10 col-xl-8 d-flex flex-column">
          <div className="mb-2">
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary d-inline-flex align-items-center gap-1"
              onClick={handleBack}
            >
              <ArrowLeft size={14} />
              {t('shared.actions.backToList')}
            </button>
          </div>
          <div className="flex-grow-1 overflow-hidden card rounded-3 p-0">
            <AutomationDetailPanel
              automationId={automationId}
              onClose={handleBack}
              onEdit={canUpdate ? (automation) => setEditing(automation) : undefined}
              onDelete={canDelete ? (automation) => deleteConfirm.request(automation) : undefined}
              reloadKey={reloadKey}
              onToggleEnabled={
                canUpdate
                  ? (automation, enabled) =>
                      new AutomationsApi().updateAutomation(automation.id, { enabled }).catch(() => null)
                  : undefined
              }
            />
          </div>
        </div>
      </div>

      <AutomationUpsertDrawer
        isOpen={editing !== undefined}
        automation={editing}
        onClose={() => setEditing(undefined)}
        onSaved={() => { setEditing(undefined); setReloadKey((key) => key + 1) }}
      />

      <ConfirmModal
        isOpen={deleteConfirm.isOpen}
        title={t('shared.actions.delete')}
        message={t('delete.confirm')}
        warning={t('shared.common.associatedDataLost')}
        confirmLabel={t('shared.actions.delete')}
        cancelLabel={t('shared.actions.cancel')}
        genericErrorLabel={t('errors.deleteFailed')}
        onClose={deleteConfirm.cancel}
        onConfirm={async () => {
          const automation = deleteConfirm.pending
          if (!automation) return
          await new AutomationsApi().deleteAutomation(automation.id)
          handleBack()
        }}
      />
    </div>
  )
}
